import http from 'axios';

import serializeParams from 'lib/utils/url';
import validate from 'lib/utils/validationRules';

import auth from '@/services/auth';
import StructHelper from '@/services/StructHelper';

import { API_URL, PAYMANT_URL } from 'root/.env';
import ProjectCardModel from 'services/project/ProjectCard';
import PartnerCard from 'services/partner/PartnerCard';

function getURLParams() {
  const rawString = window.location.search.slice(1);
  const getParams = decodeURIComponent(rawString);

  return getParams;
}

http.interceptors.response
  .use(undefined, error => {
    const result = new Promise(() => {
      if (error.response && error.response.status === 401) {
        auth.logout();
      }

      throw error.response;
    });

    return result;
  });

const CancelToken = http.CancelToken;
const source = CancelToken.source();

export default {

  PAYMANT_URL,

  getHeaders() {
    return {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    };
  },

  get(url) {
    return http.get(url, this.getHeaders());
  },
  post(url, data) {
    return http.post(url, data, this.getHeaders());
  },
  put(url, data) {
    return http.put(url, data, this.getHeaders());
  },
  delete(url) {
    return http.delete(url, this.getHeaders());
  },

  upload(data, type) {
    const headers = Object.assign(
      this.getHeaders(),
      { 'Content-Type': 'multipart/form-data' }
    );

    return http.post(`${API_URL}/protected/${type}/upload`, data, headers);
  },

  auth(data) {
    return http.post(`${API_URL}/login`, data);
  },
  registration(data) {
    return this.post(`${API_URL}/registration`, data);
  },
  checkEmail(data) {
    return this.get(`${API_URL}/check-email/${data}`);
  },

  getMerchant() {
    return this.get(`${API_URL}/protected/merchant`);
  },

  updateMerchant(data) {
    return this.put(`${API_URL}/protected/merchant`, data);
  },

  getProject() {
    return this.get(`${API_URL}/protected/project`);
  },

  async getPersonalProjects(page) {
    const { data } = await this.get(`${API_URL}/protected/projects/page/${page}?type=PERSONAL`);
    return (data || []).map(project => new ProjectCardModel(project));
  },

  async getPartnerProjects(page) {
    const { data } = await this.get(`${API_URL}/protected/projects/page/${page}?type=PARTNER`);
    return (data || []).map(project => new PartnerCard(project));
  },

  editProject(project, id) {
    return this.put(`${API_URL}/protected/project/${id}`, project);
  },
  createProject(data) {
    return this.post(`${API_URL}/protected/project`, data);
  },

  createBill(data) {
    return this.post(`${API_URL}/protected/bill/`, data);
  },
  editBill(data, id) {
    return this.put(`${API_URL}/protected/bill/${id}`, data);
  },
  sendReceipt(id, data) {
    return this.post(`${API_URL}/protected/bill/${id}/send`, data);
  },
  getBillLimits() {
    return this.get(`${API_URL}/protected/bill/limits`);
  },

  getBillPromoCodeLimits() {
    return this.get(`${API_URL}/protected/bill-promo-code-limits`);
  },
  sendBillPromoCode(data) {
    return this.post(`${API_URL}/protected/bill-promo-code`, data);
  },
  removePromoCodeById(id) {
    return this.delete(`${API_URL}/protected/bill-promo-code/${id}`);
  },
  getBillPromoCodeDetails(billCode) {
    return this.get(`${API_URL}/protected/bill/promo-code-details/${billCode}`);
  },

  payOut(data) {
    return this.post(`${API_URL}/protected/payout`, data);
  },
  getCommission(amount, params) {
    const getParams = params ? `?amount=${amount}&${params}` : `?amount=${amount}`;

    return this.get(`${API_URL}/protected/payout-details/${getParams}`);
  },
  getAvailablePayoutAmounts() {
    return this.get(`${API_URL}/protected/available-payout-amounts`);
  },

  getTransactions(page, filterBy, searchText) {
    return this.get(`${API_URL}/protected/transactions/page/${page}?${getURLParams()}&${StructHelper.getTypeFromFilter(filterBy)}${searchText && 'search=' + searchText}`);
  },

  getTransaction(publicId) {
    return this.get(`${API_URL}/protected/transaction/${publicId}`);
  },

  sendTransactionReceipt(data) {
    return this.post(`${API_URL}/protected/receipt`, data);
  },

  getGraph() {
    return this.get(`${API_URL}/protected/graph`);
  },

  telegramBotGetToken() {
    return this.post(`${API_URL}/protected/bot/telegram/auth`);
  },
  telegramBotBind({ cid, tid, sign }) {
    return this.post(`${API_URL}/protected/bot/telegram/subscribe?cid=${cid}&tid=${tid}&sign=${sign}`);
  },

  getCards() {
    return this.get(`${API_URL}/protected/card/`);
  },
  getCardStatus(token) {
    return this.get(`${API_URL}/protected/card/status?token=${token}`);
  },
  changeCard(id) {
    return this.post(`${API_URL}/protected/card/change/${id}`);
  },
  deleteCard(id) {
    return this.delete(`${API_URL}/card/${id}`);
  },
  bindCard(cardData) {
    return this.post(`${API_URL}/protected/card/`, { cardData });
  },
  confirmationCard(data) {
    return this.post(`${API_URL}/protected/card/activate`, data);
  },

  fetchQRCode(fileType, entityType, code) {
    return this.get(`${API_URL}/qr-code?fileType=${fileType}&entityType=${entityType}&code=${code}`);
  },

  projectImageUpload(file) {
    let headers = this.getHeaders();
    headers.headers['Content-Type'] = 'multipart/form-data';

    return http.post(`${API_URL}/protected/image/upload`, file, headers);
  },
  projectImageDelete() {
    return this.post(`${API_URL}/protected/image/delete`);
  },

  refund(transactionId, reason, email) {
    let params = { transactionId, reason };

    if (email) {
      params.email = email;
    }

    return this.post(`${API_URL}/protected/refund/`, params);
  },
  refundDebit(transactionId, cardId) {
    return this.post(`${API_URL}/protected/refund/debit`, { transactionId, cardId });
  },
  refundBill(publicId) {
    return this.post(`${API_URL}/protected/refund/bill`, { publicId });
  },
  getRefundedTransaction(publicId) {
    return this.get(`${API_URL}/protected/refund/child/${publicId}`);
  },
  getRefundCommission(publicId) {
    return this.get(`${API_URL}/protected/refund/commission/${publicId}`);
  },

  // types: PROJECT_URL, PAN, EMAIL
  checkBlackList(params) {
    return this.post(`${API_URL}/protected/blacklist/entry`, params);
  },

  partnerActivation(data) {
    return this.post(`${API_URL}/protected/partner/activation`, data);
  },
  partnerCreation(data) {
    return this.post(`${API_URL}/protected/partner/creation`, data);
  },
  partnerTransactionsBrowsing(projectId) {
    return this.post(`${API_URL}/protected/partner/transaction-browsing/${projectId}`);
  },
  partnerTransactionBrowsingAccess(data) {
    return this.post(`${API_URL}/protected/partner/transaction-browsing/access`, data);
  },

  selfEmployedRegistration(data) {
    return this.post(`${API_URL}/protected/self-employed/registration`, data);
  },
  selfEmployedActivation(id) {
    return this.post(`${API_URL}/protected/self-employed/activation/${id}`);
  },
  selfEmployedStatus() {
    return this.get(`${API_URL}/protected/self-employed/status`);
  },
  selfEmployedTaxServiceStatus() {
    return this.get(`${API_URL}/protected/self-employed/tax-service/status`);
  },
  selfEmployedDisconnectRequest() {
    return this.post(`${API_URL}/protected/self-employed/unbind`);
  },
  transactionDeclaration(data) {
    return this.post(`${API_URL}/protected/self-employed/declare`, data);
  },
  declarationReceipt(publicId) {
    return this.get(`${API_URL}/protected/self-employed/receipt/${publicId}`);
  },
  receiptCancelReasons() {
    return this.get(`${API_URL}/protected/self-employed/receipt/cancel/reasons`);
  },
  receiptCancel(data) {
    return this.post(`${API_URL}/protected/self-employed/receipt/cancel`, data);
  },
  selfEmployedQrCode(publicId) {
    let headers = this.getHeaders();
    headers.responseType = 'arraybuffer';

    return http.get(`${API_URL}/protected/self-employed/qr-code/${publicId}`, headers);
  },
  connectAutoDeclaration(data) {
    return this.put(`${API_URL}/protected/self-employed/auto-declaration`, data);
  },
  disconnectAutoDeclaration(projectId) {
    return this.delete(`${API_URL}/protected/self-employed/auto-declaration/${projectId}/cancel`);
  },

  fetchSelfEmployedNotifications() {
    return this.get(`${API_URL}/protected/self-employed/notifications`, {
      cancelToken: source.token,
    }).catch(thrown => {
      if (http.isCancel(thrown)) {
        console.log('Request canceled', thrown.message);
      } else {
        console.error(thrown);
      }
    });
  },
  updateSelfEmployedNotification(notificationId) {
    return this.put(`${API_URL}/protected/self-employed/notification/${notificationId}`);
  },

  getSubscriptions(page, status, search) {
    const getParams = Object.assign(
      validate.isNotEmptyString(status) && { status },
      validate.isNotEmptyString(search) && { search }
    );

    return this.get(`${API_URL}/protected/subscribers/page/${page}?${serializeParams(getParams)}`);
  },

  fetchWriteOffOptions() {
    return this.get(`${API_URL}/protected/subscription/periods`);
  },

  unfollowSubscription(subscriberId) {
    return this.post(`${API_URL}/protected/subscriber/${subscriberId}/unbind`);
  },

  fetchMarketplaceCategories() {
    return this.get(`${API_URL}/protected/marketplace-categories`);
  },

  async createWidget(widget) {
    return this.post(`${API_URL}/protected/widget/`, widget);
  },

  twoFactorAuth(data) {
    return this.post(`${API_URL}/two-factor/auth`, data);
  },
  fetchTwoFactorTypes() {
    return this.get(`${API_URL}/protected/two-factor/types`);
  },
  twoFactorSelect(type) {
    return this.post(`${API_URL}/protected/two-factor/select`, type);
  },
  twoFactorCancel() {
    return this.post(`${API_URL}/protected/two-factor/cancel`);
  },

  emailConfirmation(data) {
    return this.post(`${API_URL}/email-confirm`, data);
  },

  fetchIdentification() {
    return this.get(`${API_URL}/protected/identification/`);
  },
  updateIdentification(data) {
    return this.post(`${API_URL}/protected/identification/`, data);
  },
  fetchIdentificationCountries() {
    return this.get(`${API_URL}/protected/identification/countries`);
  },

  activatePaymentOption(data, option) {
    return this.post(`${API_URL}/protected/payment-options/${option}`, data);
  },
  disablePaymentOption(option) {
    return this.delete(`${API_URL}/protected/payment-options/${option}`);
  },
  checkActivePaymentOption(option) {
    return this.get(`${API_URL}/protected/payment-options/${option}`);
  },

  getPayoutSums(amount) {
    if (amount) {
      return this.get(`${API_URL}/protected/payout/sums?amount=${amount}`);
    }

    return this.get(`${API_URL}/protected/payout/sums`);
  },

  /*
    language - передается строковый код языка
    projectCode - код проекта
    billCode - идентификатор счета
    publicId - идентификатор платежа
  */
  getProjectLocale(getParams) {
    console.log('HTTP::getProjectLocate', getParams);
    return this.get(`${API_URL}/protected/locale?${serializeParams(getParams)}`);
  },

  // POST /v1/cabinet/protected/deal/dispute/resolve
  // {
  //   "id": 670,
  //   "code": "4fe72ebd-2d71-40d9-845d-264445433f87",
  //   "status": "OPENED",
  //   "expireDate": "2022-03-17T12:19:08",
  //   "dispute": null,
  // }
  dealDisputeResolve(data) {
    console.log('HTTP::dealDisputeResolve', data);
    return this.post(`${API_URL}/protected/deal/dispute/resolve`, data);
  },
};
